import { checkoutUrlBuilder } from '@ps/features/checkout/builders';
import { type Listing, ListingType, type WithID } from '@ps/models';

/**
 * Build the `/checkout?…` URL that launches a purchase for a PDP listing,
 * reusing the shared `checkoutUrlBuilder()` (the single source of truth for the
 * checkout param contract). Mirrors the playground catalog's product/digital
 * branching: physical → `product` intent (carries the selected variant), digital
 * → `digital` intent (no variant). The server-side resolver ultimately re-checks
 * the listing's own `checkoutType`, so this only needs to pick the right
 * store/listing/variant/qty shape.
 *
 * Returns `null` for listing types the PDP CTA doesn't check out natively
 * (external opens its own URL; meeting/token/etc. have their own entry points),
 * so the caller can fall back to its non-checkout behaviour.
 */
export function buildListingCheckoutUrl(
  listing: WithID<Listing>,
  /**
   * The creator's public `url_code` — passed explicitly (not read off the
   * listing) since it comes from the PDP route's `bioContext.urlCode`.
   */
  urlCode: string,
  options: { variantId?: string; quantity?: number; coupon?: string } = {},
): string | null {
  const { variantId, quantity, coupon } = options;

  // No urlCode → no checkout target. The builder REQUIRES a urlCode for
  // product/digital intents (`assertReady` throws otherwise). This happens in
  // the AI-builder preview, where the pseudo seller has no public url_code and
  // checkout is inert anyway — so return null (CTA no-ops) instead of throwing.
  if (!urlCode) return null;

  const withMeta = (builder: ReturnType<typeof checkoutUrlBuilder>) => {
    if (coupon) builder.coupon(coupon);
    return builder.url();
  };

  if (listing.type === ListingType.Physical) {
    return withMeta(
      checkoutUrlBuilder().product(intent => {
        intent.urlCode(urlCode).listing(listing.id);
        if (variantId) intent.variant(variantId);
        if (quantity != null) intent.quantity(quantity);
      }),
    );
  }

  if (listing.type === ListingType.Digital) {
    return withMeta(
      checkoutUrlBuilder().digital(intent => {
        intent.urlCode(urlCode).listing(listing.id);
        if (quantity != null) intent.quantity(quantity);
      }),
    );
  }

  return null;
}
